import React from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Button, TextField, IconButton } from '@material-ui/core';
import { Add, Delete, Refresh } from '@material-ui/icons';

import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';

import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';

import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import {
  getVenueWarehouses,
  getWarehouseProducts,
  getCommerceProductsAvailable,
} from 'services/stockWarehouseServices';
import { getCommercesByVenue } from 'services/commerceServices';
import { storeStockAdjustment } from 'services/stockMovementsServices';

import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';

import Venue from 'types/models/Venue';
import Commerce from 'types/models/Commerce';

import { LocationProduct, StockProductType, StockWarehouse } from 'types/models/StockReceipt';

import {
  StockMovement,
  StockMovementItem,
  StockMovementSourceType,
  StockMovementOperation,
  StockMovementReason,
} from 'types/models/StockMovement';
import { StockMovementAdjustmentFormSchema } from './schemas/StockModuleForm';
import SaveError from 'components/common/SaveError';

interface Props {
  movement?: StockMovement | null;
  isReadOnly?: boolean;
}

const EMPTY_PRODUCT: StockMovementItem = {
  id: '',
  globalProductId: 0,
  quantity: 0,
  unitCost: 0,
  createdAt: new Date().toISOString().slice(0, 10),
  stockMovementId: '',
};

export default function StockAdjustmentForm({ movement, isReadOnly }: Props): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [openDialog, setOpenDialog] = React.useState(false);

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [warehouses, setWarehouses] = React.useState<StockWarehouse[]>([]);
  const [commerces, setCommerces] = React.useState<Commerce[]>([]);
  const [locationProducts, setLocationProducts] = React.useState<LocationProduct[]>([]);

  const [products, setProducts] = React.useState<StockMovementItem[]>(
    movement?.items?.length ? movement.items : [{ ...EMPTY_PRODUCT }],
  );

  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(!isLoading && !loadError);

  React.useEffect(() => {
    let mounted = true;

    async function loadInitialData() {
      if (isVenueEditor) {
        if (mounted) setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const venuesResponse = await getVenuesWithStockModuleActive();

        if (!mounted) return;

        setVenues(venuesResponse?.data ?? []);
      } catch (error) {
        console.error(error);

        if (!mounted) return;

        setLoadError(t('stock.receipts.error.no-necessary-data'));
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      venueId: movement?.venueId ?? entity?.id ?? '',

      locationType: movement?.sourceType ?? StockMovementSourceType.WAREHOUSE,

      locationId: movement?.sourceId ?? '',

      operation: (movement as any)?.operation ?? StockMovementOperation.INCREASE,

      reason: movement?.reason ?? StockMovementReason.MANUAL_ADJUSTMENT,
    },
    validationSchema: () => StockMovementAdjustmentFormSchema(t),
    onSubmit: () => setOpenDialog(true),
  });

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setWarehouses([]);
      setCommerces([]);
      setLocationProducts([]);
      return;
    }

    async function loadVenueData() {
      try {
        const [warehousesData, commercesData] = await Promise.all([
          getVenueWarehouses(formik.values.venueId),
          getCommercesByVenue({
            venueId: formik.values.venueId,
          }),
        ]);

        setWarehouses(warehousesData);
        setCommerces(commercesData);

        if (!movement) {
          setLocationProducts([]);
          setProducts([{ ...EMPTY_PRODUCT }]);
        }
      } catch (error) {
        console.error(error);
      }
    }

    loadVenueData();
  }, [formik.values.venueId]);

  React.useEffect(() => {
    if (!formik.values.locationType || !formik.values.locationId) {
      setLocationProducts([]);
      if (!movement) setProducts([{ ...EMPTY_PRODUCT }]);
      return;
    }

    async function loadProducts() {
      try {
        if (formik.values.locationType === StockMovementSourceType.WAREHOUSE) {
          const products = await getWarehouseProducts(formik.values.locationId);

          setLocationProducts(products);
        } else {
          const products = await getCommerceProductsAvailable(formik.values.locationId);

          setLocationProducts(products);
        }
      } catch (error) {
        console.error(error);
        setLocationProducts([]);
      } finally {
        if (!movement) setProducts([{ ...EMPTY_PRODUCT }]);
      }
    }

    loadProducts();
  }, [formik.values.locationType, formik.values.locationId]);

  function updateProduct(index: number, key: keyof StockMovementItem, value: any) {
    const copy = [...products];

    copy[index] = {
      ...copy[index],
      [key]: value,
    };

    setProducts(copy);
  }

  function removeProduct(index: number) {
    if (products.length === 1) return;

    setProducts((prev) => prev.filter((_, i) => i !== index));
  }

  function clearProduct(index: number) {
    const copy = [...products];

    copy[index] = {
      ...EMPTY_PRODUCT,
    };

    setProducts(copy);
  }

  function addProduct() {
    setProducts((prev) => [...prev, { ...EMPTY_PRODUCT }]);
  }

  function getSelectedProduct(globalProductId: number | undefined | null) {
    return locationProducts.find((p) => p.globalProductId === globalProductId) ?? null;
  }

  function selectLocationProduct(index: number, globalProductId: number) {
    const product = locationProducts.find((p) => p.globalProductId === globalProductId);

    if (!product) return;

    const copy = [...products];

    copy[index] = {
      ...copy[index],
      id: product.commerceProductId ?? '',
      globalProductId: product.globalProductId,
      quantity: copy[index].quantity || 1,
      unitCost: 0,
    };

    setProducts(copy);
  }

  async function onConfirm() {
    setSaveError(null);

    try {
      await storeStockAdjustment({
        ...formik.values,
        createdByUserId: authData?.user.id,
        products,
      });

      history.go(-1);
    } catch (error) {
      console.error(error);

      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));

      formik.setSubmitting(false);
    }
  }

  if (isLoading) {
    return <div>{t('loading')}</div>;
  }

  if (loadError) {
    return <div>{loadError}</div>;
  }
  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <h1>{t('stock.forms.adjustment-title')}</h1>

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.forms.venue')}
            placeholder={t('stock.forms.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, {
              label: 'name',
            })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
          />
        )}

        <SelectField
          name="locationType"
          label={t('stock.forms.location-type')}
          placeholder={t('stock.forms.select')}
          value={formik.values.locationType}
          options={[
            {
              label: t('stock.forms.warehouse'),
              value: StockMovementSourceType.WAREHOUSE,
            },
            {
              label: t('stock.forms.commerce'),
              value: StockMovementSourceType.COMMERCE,
            },
          ]}
          isMulti={false}
          setFieldValue={(name, value) => {
            if (value !== formik.values.locationType) {
              formik.setFieldValue('locationId', '');
            }
            formik.setFieldValue(name, value);
          }}
          onBlur={formik.handleBlur}
          touched={formik.touched.locationType}
          error={formik.errors.locationType}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable
        />

        <SelectField
          name="locationId"
          label={t('stock.forms.location')}
          placeholder={t('stock.forms.select')}
          value={formik.values.locationId}
          options={mapToSelectOptions(
            formik.values.locationType === StockMovementSourceType.WAREHOUSE ? warehouses : (commerces as any),
            {
              label: 'name',
            },
          )}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.locationId}
          error={formik.errors.locationId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable
        />

        <SelectField
          name="operation"
          label={t('stock.forms.operation')}
          placeholder={t('stock.forms.select')}
          value={formik.values.operation}
          options={[
            {
              label: t('stock.forms.increase-stock'),
              value: StockMovementOperation.INCREASE,
            },
            {
              label: t('stock.forms.decrease-stock'),
              value: StockMovementOperation.DECREASE,
            },
          ]}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={(formik.touched as any).operation}
          error={(formik.errors as any).operation}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={false}
        />

        <SelectField
          name="reason"
          label={t('stock.forms.reason')}
          placeholder={t('stock.forms.reason-placeholder')}
          value={formik.values.reason}
          options={[
            {
              label: t('stock.adjustment-reasons.INVENTORY_COUNT'),
              value: StockMovementReason.INVENTORY_COUNT,
            },
            {
              label: t('stock.adjustment-reasons.MANUAL_ADJUSTMENT'),
              value: StockMovementReason.MANUAL_ADJUSTMENT,
            },
          ]}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.reason}
          error={formik.errors.reason}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={false}
        />

        <hr />

        <h3>{t('stock.forms.products')}</h3>

        {products.map((product, index) => {
          const selectedProduct = getSelectedProduct(product.globalProductId);

          return (
            <div
              key={index}
              style={{
                border: '1px solid #DDD',
                padding: 15,
                marginBottom: 15,
              }}
            >
              <SelectField
                name={`product-${index}`}
                label={t('stock.forms.product')}
                placeholder={t('stock.forms.product-placeholder')}
                value={product.globalProductId}
                options={locationProducts.map((p) => ({
                  value: p.globalProductId,
                  label: `${p.name} (${p.sku ?? ''})`,
                }))}
                isMulti={false}
                setFieldValue={(_, value) => selectLocationProduct(index, Number(value))}
                touched={false}
                error={false}
                isDisabled={formik.isSubmitting}
                isClearable
                isSearchable
                backspaceRemovesValue
                onBlur={() => undefined}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.sku')}
                value={selectedProduct?.sku ?? ''}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.name')}
                value={selectedProduct?.name ?? ''}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.type')}
                value={
                  selectedProduct
                    ? selectedProduct.type === StockProductType.FINISHED
                      ? t('stock.forms.finished-product')
                      : t('stock.forms.raw-material')
                    : ''
                }
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.current-stock')}
                value={selectedProduct?.stock ?? ''}
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                type="number"
                label={t('stock.forms.quantity')}
                value={product.quantity}
                onChange={(e) => updateProduct(index, 'quantity', Number(e.target.value))}
                inputProps={{
                  min: 0.01,
                  step: 0.01,
                  ...(formik.values.operation === StockMovementOperation.DECREASE
                    ? { max: selectedProduct?.stock ?? undefined }
                    : {}),
                  readOnly: !!movement,
                }}
              />

              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  {selectedProduct && !isReadOnly && (
                    <>
                      {formik.values.operation === StockMovementOperation.INCREASE ? (
                        <strong>
                          {t('stock.forms.stock-after-adjustment')}:{' '}
                          {Number(selectedProduct.stock) + Number(product.quantity || 0)}
                        </strong>
                      ) : (
                        <strong>
                          {t('stock.forms.stock-after-adjustment')}:{' '}
                          {Number(selectedProduct.stock) - Number(product.quantity || 0)}
                        </strong>
                      )}
                    </>
                  )}
                </div>

                {movement ? null : (
                  <div>
                    <IconButton
                      color="primary"
                      aria-label={t('stock.forms.reset-product')}
                      title={t('stock.forms.reset-product')}
                      onClick={() => clearProduct(index)}
                    >
                      <Refresh />
                    </IconButton>

                    <IconButton color="primary" onClick={addProduct}>
                      <Add />
                    </IconButton>

                    {products.length > 1 && (
                      <IconButton color="secondary" onClick={() => removeProduct(index)}>
                        <Delete />
                      </IconButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <SaveError error={saveError} />
        {!isReadOnly && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 25,
            }}
          >
            <Button variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
              {t('stock.forms.create-adjustment')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
}
